/**
 * TeoVest UI Theme Configuration
 * Intent: Provide a centralized theme configuration for consistent styling across the application.
 *
 * IMPORTANT: All component styling should be defined here in the theme, NOT in individual components.
 * Only override these styles in components when absolutely necessary for specific cases.
 *
 * Brand Colors:
 * - Primary Purple: #470047
 * - Accent Orange: #ffb94e
 *
 * Colors, fonts, radius, spacing, and font sizes are DERIVED from `@teo/design-tokens`
 * via the semantic→Mantine adapter — there is no hand-maintained hex here.
 * The brand purple/orange/green ramps are byte-identical to the values this file used
 * to hardcode; `red` is now a real 10-step gradation (it was a flat `#b63333` ×10 fill
 * that could not express hover/active/disabled states). The radius/spacing/fontSize/
 * lineHeight token scales equal Mantine 8's defaults, so wiring them changes no rendered
 * value. Elevation is deliberately NOT wired into `theme.shadows` — Paper keeps Mantine's
 * default shadow (see adapter). Light-mode only: TEO has no dark mode.
 *
 * The `components` block below is NOT derivable from the tokens (it carries non-color
 * scalars like `Text` `opacity`, and component-specific Mantine styling) and is preserved
 * verbatim.
 */
import { Input, createTheme, getFontSize } from '@mantine/core';

import {
  THEME_BLACK,
  THEME_FONT_FAMILY,
  THEME_HEADING_FONT_FAMILY,
  THEME_HEADING_FONT_WEIGHT,
  THEME_HEADING_SIZES,
  THEME_WHITE,
  tokensToFontSizes,
  tokensToLineHeights,
  tokensToMantineColors,
  tokensToRadius,
  tokensToSpacing,
} from './theme/adapter';

export const theme = createTheme({
  primaryColor: 'purple',
  colors: tokensToMantineColors(),
  fontFamily: THEME_FONT_FAMILY,
  headings: {
    fontFamily: THEME_HEADING_FONT_FAMILY,
    fontWeight: THEME_HEADING_FONT_WEIGHT,
    // Fluid: each size is a clamp() whose maximum equals the Mantine 8 default,
    // so desktop is unchanged and phones get a smaller heading with no media
    // query. Body text and the t-shirt scale deliberately do not move — see the
    // headingSize token group for why.
    sizes: THEME_HEADING_SIZES,
  },
  defaultRadius: 'md',
  radius: tokensToRadius(),
  spacing: tokensToSpacing(),
  fontSizes: tokensToFontSizes(),
  lineHeights: tokensToLineHeights(),
  white: THEME_WHITE,
  black: THEME_BLACK,

  components: {
    // Interactive Elements
    Button: {
      defaultProps: {
        variant: 'outline',
        color: 'purple',
      },
      styles: {
        root: {
          fontWeight: 600,
        },
      },
    },
    Anchor: {
      defaultProps: {
        underline: 'hover',
        c: 'orange.9',
        fw: 600,
      },
    },

    // Form Inputs
    //
    // iOS Safari zooms the whole page in when a focused input's text is under
    // 16px, and does not zoom back out on blur — so a single tap on any field
    // leaves the user panned and scrolling sideways. Mantine's Input defaults to
    // size="sm" (14px) and 47 call sites ask for "xs" (12px), so every text
    // field in the app triggers it.
    //
    // The floor lives on `Input`, not on TextInput/Select/NumberInput/… one by
    // one, because each of those renders the shared inner `Input`, which
    // registers "Input" as a theme name alongside its own — one entry reaches
    // the whole family, including components not used yet.
    //
    // Two things this deliberately does NOT do. It does not raise
    // `theme.fontSizes.sm`, which would drag every size="sm" Text and Badge
    // along with it. It does not bump `defaultProps.size`, because `size` also
    // drives --input-height and padding, which would change the height of every
    // control in every form.
    //
    // What it costs, on two axes, and nothing here is a no-op.
    //
    // HEIGHT holds for single-line inputs only: they pin `height` to
    // --input-height, so the box does not move (measured at 0.00px). Anything
    // multiline is content-sized instead, because Mantine's [data-multiline]
    // rule sets --input-size:auto. For a Textarea that means 1.55 × the size's
    // step per rendered row — about 3px at sm, about 6px at xs; a two-row sm
    // Textarea measures 56.4px before and 62.6px after. MultiSelect, PillsInput
    // and TagsInput are multiline too but grow by a different rule: their field
    // is 1.6em, so it tracks the type, while the pills inside it are pinned in
    // px to --pill-height-<size> and do not move at all.
    //
    // WIDTH moves for single-line inputs too, which is the trap, because the
    // height figure above invites the opposite conclusion. The input is
    // width:100%, so anywhere its wrapper is content-sized rather than
    // stretched — a Group, any shrink-to-fit flex row — the control gets wider
    // with the type. Measured: an xs DateInput in the board-package toolbar
    // 169.5px → 218px, a Select on the same page 223.5px → 244px.
    //
    // So the thing to check after touching this is any input that has to line
    // up with a control beside it, single-line or not.
    //
    // `max()` rather than a flat 1rem so this stays a floor: a caller that
    // asks for a deliberately larger input keeps it.
    //
    // Two known holes, and these are the only two — a sweep for raw
    // <input>/<textarea>/<select> finds six, of which four are hidden fields or
    // test mocks.
    //
    // 1. `JsonInput` renders [data-monospace], and Mantine's CSS reads
    //    `font-size: var(--_input-fz, var(--input-fz, …))` while that attribute
    //    sets --_input-fz to --input-fz minus 2px, so it lands at 14px. No call
    //    sites today; the first one to add it must floor --_input-fz too.
    // 2. ChangeStatusModal renders a bare <input type="date"> with an inline
    //    fontSize, so it carries none of Mantine's Input classes and no entry
    //    here can reach it. It zooms today.
    //
    // Anything that opts out of Mantine's Input is outside this floor by
    // construction; the fix for that class is to stop hand-rolling the input.
    Input: Input.extend({
      vars: (_theme, props) => ({
        wrapper: { '--input-fz': `max(1rem, ${getFontSize(props.size ?? 'sm')})` },
      }),
    }),

    // Labels carry the brand purple (matches the mockup's `--purple-7` field
    // labels). Descriptions / helper text are deliberately the neutral `dimmed`
    // token (the mockup's `--muted`), NOT purple — purple-on-purple helper text
    // is the "purple mud" we are avoiding. Setting it explicitly here keeps every
    // input's helper text neutral rather than relying on the Mantine default.
    TextInput: {
      defaultProps: {
        radius: 'md',
      },
      styles: () => ({
        label: { color: 'var(--mantine-color-purple-7)' },
        description: { color: 'var(--mantine-color-dimmed)' },
        input: { '&:focus': { borderColor: 'var(--mantine-color-purple-7)' } },
      }),
    },
    PasswordInput: {
      defaultProps: {
        radius: 'md',
      },
      styles: () => ({
        label: { color: 'var(--mantine-color-purple-7)' },
        description: { color: 'var(--mantine-color-dimmed)' },
        input: { '&:focus': { borderColor: 'var(--mantine-color-purple-7)' } },
      }),
    },

    // Typography
    Title: {
      defaultProps: {
        c: 'purple.7',
      },
    },
    Text: {
      defaultProps: {
        c: 'purple.7',
        opacity: 0.85,
      },
    },

    // Mantine 9 changed Portal's default to `reuseTargetNode: true`: every
    // portal renders into one shared <div data-mantine-shared-portal-node> that
    // is never removed, where Mantine 8 gave each portal its own node and
    // deleted it on cleanup. When the component owning an overlay unmounts
    // while that overlay is still mounted — a card in a list that reorders
    // under it, say — the overlay's DOM is left behind in the shared node, so
    // the app keeps a dialog in the accessibility tree that nothing owns and
    // nothing will ever close. It is sometimes fully opaque, which is a visible
    // stuck modal, and sometimes frozen mid-transition at opacity 0, which is
    // worse: invisible to a person and still `role="dialog"` to a screen reader.
    //
    // Restoring the per-portal node makes the cleanup that removes it run
    // again. Set on Portal rather than on Modal so every overlay gets it, and
    // because the halfway version is worse than either end: with only modals
    // moved out, a modal's node is appended to <body> after the shared node the
    // popovers still use, which silently inverts the document order of the two.
    // At Portal level the order is by mount time, which is what it was in
    // Mantine 8. This is behaviour rather than styling, and it lives here
    // because the trap is a library default, not anything one component does.
    Portal: {
      defaultProps: {
        reuseTargetNode: false,
      },
    },

    // Containers
    Paper: {
      defaultProps: {
        shadow: 'sm',
        radius: 'md',
        bg: 'white',
      },
    },
  },
});
